import React, { FC, useCallback, useMemo } from "react";

import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import { GuideType } from "@mapmycustomers/shared";

import RoundButton from "@app/component/RoundButton";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getGuideTypeDisplayName } from "@app/util/ui";

import styles from "./TypeFilter.module.scss";

const CLEAR_OPTION_VALUE = "clear";

export interface TypeFilterProps {
  onChange: (value: GuideType | undefined) => void;
  value?: GuideType;
}

const TypeFilter: FC<TypeFilterProps> = ({ onChange, value }) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const handleMenuClick = useCallback(
    (info: MenuInfo) => {
      analytics.using(["Type Filter"]);
      const value = info.key as GuideType | typeof CLEAR_OPTION_VALUE;
      if (value === CLEAR_OPTION_VALUE) {
        onChange(undefined);
      } else {
        onChange(value);
      }
    },
    [analytics, onChange]
  );

  const menuProps = useMemo(() => {
    const items: MenuProps["items"] = [
      {
        className: styles.clearMenuItem,
        key: CLEAR_OPTION_VALUE,
        label: intl.formatMessage({
          id: "onboarding.guides.filters.type.options.clear",
          defaultMessage: "Clear",
          description: "Clear option in the Type filter button on the Guides page",
        }),
      },
      ...[GuideType.WEB, GuideType.MOBILE].map((guideType) => ({
        key: guideType,
        label: getGuideTypeDisplayName(intl, guideType),
      })),
    ];
    return {
      activeKey: value,
      items,
      onClick: handleMenuClick,
    };
  }, [handleMenuClick, intl, value]);

  return (
    <Dropdown menu={menuProps} overlayClassName={styles.dropdownOverlay} trigger={["click"]}>
      <RoundButton active={!!value} hasMenu>
        {intl.formatMessage(
          {
            id: "onboarding.guides.filters.type.title",
            defaultMessage: "Type{hasValue, select, true {: {value}} other {}}",
            description: "Title of the Type filter button on the Guides page",
          },
          {
            hasValue: !!value,
            value: value ? getGuideTypeDisplayName(intl, value) : undefined,
          }
        )}
      </RoundButton>
    </Dropdown>
  );
};

export default TypeFilter;
